import { Edit3, Circle, Trash2, X } from 'lucide-react';

interface AnnotatePanelProps {
  isMobileView: boolean;
  selectedAnnotationTool: 'pencil' | 'square' | 'circle' | 'arrow' | null;
  selectedColor: string;
  setSelectedAnnotationTool: (
    tool: 'pencil' | 'square' | 'circle' | 'arrow' | null
  ) => void;
  setSelectedColor: (color: string) => void;
  setActivePanel: (panel: 'chat' | 'notes' | 'annotate' | null) => void;
  setIsAnnotating: (val: boolean) => void;
  clearAnnotations: () => void;
  handleAnnotationToolSelect: (tool: 'pencil' | 'square' | 'circle' | 'arrow') => void;
}

// Removed Blue color for mobile optimization
const COLORS = [
  { value: '#ff0000', name: 'Red' },
  { value: '#000000', name: 'Black' },
  { value: '#ffff00', name: 'Yellow' },
];

export function AnnotatePanel({
  isMobileView,
  selectedAnnotationTool,
  selectedColor,
  setSelectedAnnotationTool,
  setSelectedColor,
  setActivePanel,
  setIsAnnotating,
  clearAnnotations,
  handleAnnotationToolSelect,
}: AnnotatePanelProps) {
  // Mobile: float at top (above everything), slightly larger for better usability
  // Desktop: float above toolbar at bottom (changed from bottom-24 to bottom-32 for more space)
  const baseClasses = isMobileView
    ? 'fixed left-1/2 -translate-x-1/2 z-[100] bg-black/80 backdrop-blur-xl rounded-full px-2.5 py-1.5 flex items-center gap-1 border border-white/20 shadow-2xl text-xs w-auto'
    : 'fixed left-1/2 -translate-x-1/2 bottom-32 z-[100] bg-black/70 backdrop-blur-xl rounded-full px-3 py-2 flex items-center gap-2 border border-white/20 shadow-2xl';

  return (
    <div
      data-panel="annotate"
      className={baseClasses}
      style={{
        top: isMobileView ? 'max(8px, env(safe-area-inset-top, 8px))' : undefined,
        maxWidth: isMobileView
          ? 'calc(100vw - max(32px, env(safe-area-inset-left, 16px) + env(safe-area-inset-right, 16px)))'
          : undefined,
        paddingTop: isMobileView ? 'max(0px, env(safe-area-inset-top, 0px))' : undefined,
        whiteSpace: isMobileView ? 'nowrap' : undefined,
        overflowX: isMobileView ? 'hidden' : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Label - Hidden on mobile to save space */}
      {!isMobileView && (
        <div className="flex items-center gap-1 pr-2 border-r border-white/20">
          <Edit3 className="h-4 w-4 text-white/80" strokeWidth={2} />
          <span className="text-xs font-medium text-white/90">Annotate</span>
        </div>
      )}

      {/* Tools - Removed Square and Arrow for mobile optimization */}
      <div className={`flex items-center flex-nowrap ${isMobileView ? 'gap-1' : 'gap-1 pl-2 pr-2'} ${!isMobileView ? 'border-r border-white/20' : ''}`}>
        {/* Pencil */}
        <button
          onClick={() => {
            setSelectedAnnotationTool('pencil');
            setIsAnnotating(true);
            handleAnnotationToolSelect('pencil');
          }}
          className={`${isMobileView ? 'w-8 h-8' : 'w-8 h-8'} shrink-0 rounded-full flex items-center justify-center text-white/80 hover:bg-white/10 transition-all ${
            selectedAnnotationTool === 'pencil' ? 'bg-white/20' : ''
          }`}
          title="Pen"
        >
          <Edit3 className={isMobileView ? "h-4 w-4" : "h-4 w-4"} strokeWidth={2} />
        </button>

        {/* Circle */}
        <button
          onClick={() => {
            setSelectedAnnotationTool('circle');
            setIsAnnotating(true);
            handleAnnotationToolSelect('circle');
          }}
          className={`${isMobileView ? 'w-8 h-8' : 'w-8 h-8'} shrink-0 rounded-full flex items-center justify-center text-white/80 hover:bg-white/10 transition-all ${
            selectedAnnotationTool === 'circle' ? 'bg-white/20' : ''
          }`}
          title="Circle"
        >
          <Circle className={isMobileView ? "h-4 w-4" : "h-4 w-4"} strokeWidth={2} />
        </button>
      </div>

      {/* Colors - Removed Blue for mobile optimization */}
      <div className={`flex items-center flex-nowrap ${isMobileView ? 'gap-1' : 'gap-1 px-2'} ${!isMobileView ? 'border-r border-white/20' : ''}`}>
        {COLORS.map((color) => (
          <button
            key={color.value}
            onClick={() => setSelectedColor(color.value)}
            className={`${isMobileView ? 'w-6 h-6' : 'w-7 h-7'} shrink-0 rounded-full border-2 transition-all ${
              selectedColor === color.value
                ? 'border-white scale-110'
                : 'border-white/30 hover:border-white/50'
            }`}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>

      {/* Clear */}
      <button
        onClick={() => {
          clearAnnotations();
          setSelectedAnnotationTool(null);
          setIsAnnotating(false);
          setActivePanel(null);
        }}
        className={`${isMobileView ? 'w-8 h-8' : 'w-9 h-9'} shrink-0 rounded-full flex items-center justify-center text-red-300 hover:bg-red-500/20 hover:text-red-400 transition-all`}
        title="Clear & close"
      >
        <Trash2 className={isMobileView ? 'h-4 w-4' : 'h-4 w-4'} strokeWidth={2} />
      </button>

      {/* Close */}
      <button
        onClick={() => {
          setActivePanel(null);
          setIsAnnotating(false);
        }}
        className={`${isMobileView ? 'w-8 h-8' : 'w-9 h-9'} shrink-0 rounded-full flex items-center justify-center text-white/60 hover:bg-white/10 transition-all`}
        title="Close"
      >
        <X className={isMobileView ? 'h-4 w-4' : 'h-4 w-4'} strokeWidth={2} />
      </button>
    </div>
  );
}
